import React from "react";
import { useTranslation, Trans } from "react-i18next";
import i18n from "i18n";
import CopyButton from "components/CopyButton";
import { IconBrush, IconCopy, IconDesign, IconLasso, IconMagicSelection, IconModel, IconMood, IconRemoveBg, IconStyle, IconTip } from "icons";
import { ImageSketchExample } from "images";
import Tab from "components/Tab";

////// DESIGN

const TabContent = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const prompts = [t("common:tutorial.design_flow.tab_design_prompt"), t("common:tutorial.design_flow.tab_model_prompt"), t("common:tutorial.design_flow.tab_background_prompt")];

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-[5px] text-headline uppercase text-white bg-black-300 p-2 rounded-t-[5px]">
        <IconTip className="w-[14px] h-[14px]" /> {t("common:tutorial.design_flow.try_this_prompt")}
      </div>
      <div className="flex flex-col gap-3 rounded-b-[5px] p-3 border border-black-300 bg-black">
        <div className="flex w-full gap-[5px]">
          <Tab
            initTab={0}
            onChange={(value: any) => {
              setCurrentIndex(value);
            }}
          >
            <Tab.Item id={0}>
              <div className="flex-center gap-[5px] w-full">
                <IconDesign className="w-[18px] h-[18px]" />
                {t("common:tutorial.design_flow.tab_define_design")}
              </div>
            </Tab.Item>
            <Tab.Item id={1}>
              <div className="flex-center gap-[5px] w-full">
                <IconModel className="w-[18px] h-[18px]" />
                {t("common:tutorial.design_flow.tab_model")}
              </div>
            </Tab.Item>
            <Tab.Item id={2}>
              <div className="flex-center gap-[5px] w-full">
                <IconRemoveBg className="w-[18px] h-[18px]" />
                {t("common:tutorial.design_flow.tab_background")}
              </div>
            </Tab.Item>
          </Tab>
        </div>
        <span className="text-bodySm text-white">{prompts[currentIndex]}</span>
        <CopyButton copyData={prompts[currentIndex]} className="self-end h-[18px] w-fit text-h7 text-green hover:text-white">
          <IconCopy className="w-4 h-4" /> {t("common:tutorial.design_flow.copy_prompt")}
        </CopyButton>
      </div>
    </div>
  );
};

const AddThisSketch = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col w-full">
      <div className="flex gap-[5px] text-headline text-white uppercase bg-black-300 p-2 rounded-t-[5px]">
        <IconTip className="w-[14px] h-[14px]" /> {t("common:tutorial.design_flow.try_this_sketch")}
      </div>
      <div className="flex flex-col p-3 gap-3 border-b border-x border-black-300 rounded-b-[5px]">
        <img src={ImageSketchExample} className="rounded-[3px] h-[90px] w-[90px]" />
      </div>
    </div>
  );
};

const HighlightedSpan = ({ i18nKey }: { i18nKey: string }) => (
  <span>
    <Trans i18nKey={i18nKey} components={{ highlight: <span className="text-purple dark:text-green" /> }} />
  </span>
);

const StepNewDesignDescription = () => <HighlightedSpan i18nKey="common:tutorial.design_flow.step_new_design_description" />;

const StepDesignTrainingDescription = () => <HighlightedSpan i18nKey="common:tutorial.design_flow.step_training_description" />;

const StepNewTrainingDescription = () => <HighlightedSpan i18nKey="common:tutorial.train_flow.step_new_training_description" />;

const StepTrainingTypeDescription = () => <HighlightedSpan i18nKey="common:tutorial.train_flow.step_training_type_description" />;

const StepChooseDesignDescription = () => <HighlightedSpan i18nKey="common:tutorial.edit_flow.step_choose_design_description" />;

const StepEditActionDescription = () => <HighlightedSpan i18nKey="common:tutorial.edit_flow.step_action_description" />;

const StepDescribeDescription = () => <HighlightedSpan i18nKey="common:tutorial.edit_flow.step_describe_description" />;

const TrainingModelsContent = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col w-full">
      <div className="flex gap-[5px] text-headline text-white uppercase bg-black-300 p-2 rounded-t-[5px]">
        <IconTip className="w-[14px] h-[14px]" /> {t("common:tutorial.train_flow.training_models_heading")}
      </div>
      <div className="flex flex-col p-3 gap-3 border-b border-x border-black-300 rounded-b-[5px]">
        <div className="flex gap-2.5 items-center">
          <IconStyle className="text-trueBlack dark:text-green flex-shrink-0 w-4 h-4" />
          <span className="text-h7 text-white">
            <b>{t("common:tutorial.train_flow.look_training_label")}</b>
            {t("common:tutorial.train_flow.look_training_description")}
          </span>
        </div>
        <div className="flex gap-2.5 items-center">
          <IconMood className="text-orange flex-shrink-0 w-4 h-4" />
          <span className="text-h7 text-white">
            <b>{t("common:tutorial.train_flow.mood_training_label")}</b>
            {t("common:tutorial.train_flow.mood_training_description")}
          </span>
        </div>
      </div>
    </div>
  );
};

const SelectionToolsContent = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col w-full">
      <div className="flex gap-[5px] text-headline text-white uppercase bg-black-300 p-2 rounded-t-[5px]">
        <IconTip className="w-[14px] h-[14px]" /> {t("common:tutorial.edit_flow.selection_tools_heading")}
      </div>
      <div className="flex flex-col p-3 gap-3 border-b border-x border-black-300 rounded-b-[5px]">
        <div className="flex gap-2.5 items-center">
          <IconBrush className="text-green flex-shrink-0 w-4 h-4" />
          <span className="text-h7 text-white">
            <b>{t("common:tutorial.edit_flow.brush_tool_label")}</b>
            {t("common:tutorial.edit_flow.brush_tool_description")}
          </span>
        </div>
        <div className="flex gap-2.5 items-center">
          <IconMagicSelection className="text-green flex-shrink-0 w-4 h-4" />
          <span className="text-h7 text-white">
            <b>{t("common:tutorial.edit_flow.magic_selection_label")}</b>
            {t("common:tutorial.edit_flow.magic_selection_description")}
          </span>
        </div>
        <div className="flex gap-2.5 items-center">
          <IconLasso className="text-green flex-shrink-0 w-4 h-4" />
          <span className="text-h7 text-white">
            <b>{t("common:tutorial.edit_flow.lasso_tool_label")}</b>
            {t("common:tutorial.edit_flow.lasso_tool_description")}
          </span>
        </div>
      </div>
    </div>
  );
};

// Headers/descriptions render as functions so each call site resolves a fresh translation.
// Uses `i18n.t(...)` direct call rather than the `useTranslation` hook because the function is
// invoked from `TutorialItem.tsx` inside render but at a deferred call site — rules-of-hooks
// would flag a hook call here, and `i18n.t` re-executes each invocation so no stale capture.
const headerKey = (key: string) => () => i18n.t(key);

export const DesignTutorialData: any = [
  {
    image: "Dummy",
    header: "Dummy",
    description: "Dummy",
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Design/Design-Step-1.mp4",
    header: headerKey("common:tutorial.design_flow.step_new_design_header"),
    description: () => <StepNewDesignDescription />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Design/Design-Step-2.mp4",
    header: headerKey("common:tutorial.design_flow.step_style_header"),
    description: "",
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Design/Design-Step-3.mp4",
    header: headerKey("common:tutorial.design_flow.step_training_header"),
    description: () => <StepDesignTrainingDescription />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Design/Design-Step-4.mp4",
    header: headerKey("common:tutorial.design_flow.step_prompt_header"),
    description: headerKey("common:tutorial.design_flow.step_prompt_description"),
    content: () => {
      return <TabContent />;
    },
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Design/Design-Step-5.mp4",
    header: headerKey("common:tutorial.design_flow.step_sketch_header"),
    description: headerKey("common:tutorial.design_flow.step_sketch_description"),
    content: () => <AddThisSketch />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Design/Design-Step-6.mp4",
    header: headerKey("common:tutorial.design_flow.step_create_header"),
    description: headerKey("common:tutorial.design_flow.step_create_description"),
  },
];

////// TRAIN

export const TrainTutorialData: any = [
  {
    image: "Dummy",
    header: "Dummy",
    description: "Dummy",
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Train/Train-Step-1.mp4",
    header: headerKey("common:tutorial.train_flow.step_new_training_header"),
    description: () => <StepNewTrainingDescription />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Train/Train-Step-2.mp4",
    header: headerKey("common:tutorial.train_flow.step_training_type_header"),
    description: () => <StepTrainingTypeDescription />,
    content: () => <TrainingModelsContent />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Train/Train-Step-3.mp4",
    header: headerKey("common:tutorial.train_flow.step_name_header"),
    description: headerKey("common:tutorial.train_flow.step_name_description"),
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Train/Train-Step-4.mp4",
    header: headerKey("common:tutorial.train_flow.step_subject_header"),
    description: headerKey("common:tutorial.train_flow.step_subject_description"),
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Train/Train-Step-5.mp4",
    header: headerKey("common:tutorial.train_flow.step_upload_header"),
    description: headerKey("common:tutorial.train_flow.step_upload_description"),
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Train/Train-Step-6.mp4",
    header: headerKey("common:tutorial.train_flow.step_start_header"),
    description: "",
  },
];

////// EDIT

export const EditImageTutorialData: any = [
  {
    image: "Dummy",
    header: "Dummy",
    description: "Dummy",
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Edit/Edit-Step-1.mp4",
    header: headerKey("common:tutorial.edit_flow.step_choose_design_header"),
    description: () => <StepChooseDesignDescription />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Edit/Edit-Step-2.mp4",
    header: headerKey("common:tutorial.edit_flow.step_instrument_header"),
    description: headerKey("common:tutorial.edit_flow.step_instrument_description"),
    content: () => <SelectionToolsContent />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Edit/Edit-Step-3.mp4",
    header: headerKey("common:tutorial.edit_flow.step_select_area_header"),
    description: "",
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Edit/Edit-Step-4.mp4",
    header: headerKey("common:tutorial.edit_flow.step_action_header"),
    description: () => <StepEditActionDescription />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Edit/Edit-Step-5.mp4",
    header: headerKey("common:tutorial.edit_flow.step_describe_header"),
    description: () => <StepDescribeDescription />,
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Edit/Edit-Step-6.mp4",
    header: headerKey("common:tutorial.edit_flow.step_try_header"),
    description: "",
  },
  {
    image: "https://storage.googleapis.com/refabric-static/cdn/Refabric%20Onboarding%20Videos/Edit/Edit-Step-7.mp4",
    header: headerKey("common:tutorial.edit_flow.step_remove_bg_header"),
    description: headerKey("common:tutorial.edit_flow.step_remove_bg_description"),
  },
];
